import Close from '@ixigo/icons/Close';
import Button from '@ixigo/iui/Button';
import { BottomsheetExtended } from '@repo/shared/components';
import cn from '@repo/shared/utils/cn';
import { useEffect, useRef, useState } from 'react';
import type { FareDetailErrorType, FareDetailsErrorResponse } from '@/api';
import cabErrorImage from '@/assets/api-error.png?url';
import { NavigationHeader } from '@/components';
import {
	CabPicker,
	type CabPickerProps,
	type CabSelectionOptions,
} from '@/components/cab-selection/';
import {
	FARE_DETAIL_ERROR_MAP,
	FareDetailView,
} from '@/components/error-states';
import type { SearchFormData } from '@/components/search-form';
import { useTracker } from '@/lib/analytics/provider';
import { useReviewPageActions } from '@/routes/review/-store';
import { useFareDetail } from './hooks';
import Loader from './loader';
import { getCabAttributes } from './utils';

export interface CabSelectionPickerProps {
	showOptions: boolean;
	onClose: () => void;
	onConfirm: (cabId: string) => void;
	defaultCabOptions: CabSelectionOptions;
	selectedCabId?: string;
	burnOpted?: CabPickerProps['burnOpted'];
}

export function CabSelectionPicker({
	showOptions,
	onClose,
	onConfirm,
	defaultCabOptions,
	selectedCabId: defaultSelectedCabId,
	burnOpted,
}: CabSelectionPickerProps) {
	const { track } = useTracker();

	const exitCallbackRef = useRef<() => void>(null);
	const [selectedCabId, setSelectedCabId] = useState(
		defaultSelectedCabId || '',
	);
	const [showCabPicker, setShowCabPicker] = useState(showOptions || false);
	const { setSelectedCab } = useReviewPageActions();

	useEffect(() => {
		setShowCabPicker(showOptions);
		setSelectedCabId(defaultSelectedCabId || '');
	}, [showOptions, defaultSelectedCabId]);

	const handleClose = () => {
		onClose();
		setSelectedCabId(selectedCabId);
	};
	const handleConfirm = () => {
		handleClose();
		exitCallbackRef.current = () => {
			const selectedCab = defaultCabOptions.options.find(
				(cab) => cab.cabId === selectedCabId,
			);
			if (selectedCabId && selectedCab) {
				track('Cab Type Selected', getCabAttributes(selectedCab));
				setSelectedCab(selectedCab);
				onConfirm(selectedCabId);
			}
		};
	};

	return (
		<BottomsheetExtended
			open={showCabPicker}
			onClose={handleClose}
			TransitionProps={{
				onExited: () => {
					if (exitCallbackRef.current) {
						exitCallbackRef.current();
						exitCallbackRef.current = null;
					}
				},
			}}
			classes={{
				container: 'h-auto pb-safe no-scrollbar',
			}}
		>
			<div className="flex flex-col">
				<NavigationHeader
					title="Choose a cab"
					className={cn(
						'border-b border-tertiary sticky top-0 z-[1] bg-primary rounded-t-[30px]',
					)}
					leftIcon={<Close />}
					leftAction={handleClose}
				/>

				<CabPicker
					onChange={(selectedCabId) => {
						setSelectedCabId(selectedCabId);
					}}
					cabSelectionOptions={defaultCabOptions}
					selectedCabId={selectedCabId}
					burnOpted={burnOpted}
				/>

				<div className="sticky bottom-0 w-full bg-primary border-t border-tertiary px-20 py-2.5">
					<Button
						variant="solid"
						color="brand"
						size="lg"
						className="w-full"
						onClick={handleConfirm}
						disabled={!selectedCabId}
					>
						Confirm
					</Button>
				</div>
			</div>
		</BottomsheetExtended>
	);
}

export interface CabSelectionWithDataProps {
	showOptions: boolean;
	onClose: () => void;
	onConfirm: (cabId: string) => void;
	formData: Required<SearchFormData> | null;
}

export function CabSelectionWithData({
	showOptions,
	onClose,
	onConfirm,
	formData,
}: CabSelectionWithDataProps) {
	const { track } = useTracker();

	const exitCallbackRef = useRef<(() => void) | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);
	const [selectedCabId, setSelectedCabId] = useState('');
	const [showCabPicker, setShowCabPicker] = useState(showOptions || false);
	const { setFareDetails, setSelectedCab } = useReviewPageActions();

	// Create abort controller for fare details request
	useEffect(() => {
		if (showCabPicker && formData) {
			abortControllerRef.current = new AbortController();
		}
		return () => {
			abortControllerRef.current?.abort();
		};
	}, [showCabPicker, formData]);

	const { loading, error, fareDetailsResponse } = useFareDetail(
		showCabPicker && formData ? formData : null,
		{
			signal: abortControllerRef.current?.signal,
		},
	);

	const cabOptions: CabSelectionOptions | undefined =
		fareDetailsResponse?.tripDetails?.cabSelection;

	useEffect(() => {
		setShowCabPicker(showOptions);
	}, [showOptions]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: setFareDetails is an action
	useEffect(() => {
		if (showCabPicker && cabOptions && Object.keys(cabOptions).length > 0) {
			setFareDetails(fareDetailsResponse);
		}
	}, [cabOptions, fareDetailsResponse]);

	const handleClose = () => {
		abortControllerRef.current?.abort();
		onClose();
	};

	const handleErrorAction = (_type: FareDetailErrorType) => {
		setShowCabPicker(false);
		exitCallbackRef.current = () => {
			onClose();
		};
	};
	const getErrorProps = (errorObj: FareDetailsErrorResponse) => {
		const { message, title, type } = errorObj.errors;
		const errorMap = {
			...FARE_DETAIL_ERROR_MAP,
			GENERIC_ERROR: { image: cabErrorImage, btnText: 'Try Again' },
		};

		return {
			title,
			description: message,
			errorType: type,
			errorMap,
			action: () => handleErrorAction(type),
		};
	};

	const handleConfirm = () => {
		handleClose();
		exitCallbackRef.current = () => {
			const selectedCab = cabOptions.options.find(
				(cab) => cab.cabId === selectedCabId,
			);

			if (selectedCabId && selectedCab) {
				track('Cab Type Selected', getCabAttributes(selectedCab));
				setSelectedCab(selectedCab);
				onConfirm(selectedCabId);
			}
		};
	};
	const renderContent = () => {
		if (error) {
			const { title, description, errorType, errorMap, action } =
				getErrorProps(error);
			return (
				<FareDetailView
					title={title}
					description={description}
					errorType={errorType}
					onAction={action}
					fullScreen={false}
					errorMap={errorMap}
				/>
			);
		}
		return (
			<CabPicker
				onChange={(selectedCabId) => {
					setSelectedCabId(selectedCabId);
				}}
				cabSelectionOptions={cabOptions}
				selectedCabId={selectedCabId}
			/>
		);
	};

	return (
		<BottomsheetExtended
			open={showCabPicker}
			onClose={handleClose}
			TransitionProps={{
				onExited: () => {
					if (exitCallbackRef.current) {
						exitCallbackRef.current();
						exitCallbackRef.current = null;
					}
				},
			}}
			classes={{
				container: 'h-auto pb-safe no-scrollbar',
			}}
		>
			<div className="flex flex-col">
				<NavigationHeader
					title={`${loading ? 'Fetching Cabs...' : 'Choose a cab'}`}
					className={cn(
						'border-b border-tertiary sticky top-0 z-[1] bg-primary rounded-t-[30px]',
						error && 'hidden',
					)}
					leftIcon={<Close />}
					leftAction={handleClose}
				/>
				{loading ? <Loader /> : renderContent()}
				<div
					className={cn(
						'sticky bottom-0 w-full bg-primary border-t border-tertiary px-20 py-2.5',
						error && 'hidden',
					)}
				>
					<Button
						variant="solid"
						color="brand"
						size="lg"
						className="w-full"
						onClick={handleConfirm}
						disabled={!selectedCabId || loading}
					>
						Confirm
					</Button>
				</div>
			</div>
		</BottomsheetExtended>
	);
}

export default CabSelectionPicker;
